"use client";

import type { Dayjs } from "dayjs";
import { dayjs } from "utils";

import type { ReportData } from "../ReportItem/ReportItem";
import {
  AgentReport,
  AgentReportLevelEnum,
} from "@/src/features/api/memberApi";

export type ReportTimelineItem = ReportData;

export type ReportsTimelineGroupedData = Record<string, ReportTimelineItem[]>;

type MaybeString = string | number | null | undefined;

const SCORE_ORDER = [
  "F-",
  "F",
  "F+",
  "D-",
  "D",
  "D+",
  "C-",
  "C",
  "C+",
  "B-",
  "B",
  "B+",
  "A-",
  "A",
  "A+",
  "S",
  "S+",
];

const LEVEL_RANK: Record<AgentReportLevelEnum, number> = {
  [AgentReportLevelEnum.LOW]: 1,
  [AgentReportLevelEnum.MID]: 2,
  [AgentReportLevelEnum.HIGH]: 3,
};

const normalizeScoreValue = (value: MaybeString): number => {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  const normalized = String(value).trim().toUpperCase();
  if (!normalized) {
    return 0;
  }
  const index = SCORE_ORDER.indexOf(normalized);
  if (index >= 0) {
    return index;
  }
  const numeric = Number.parseFloat(normalized.replace(/[^0-9.-]/g, ""));
  if (Number.isFinite(numeric)) {
    return numeric;
  }
  return normalized.charCodeAt(0);
};

const determineRatingTrend = (
  current?: MaybeString,
  previous?: MaybeString,
  fallbackLevel?: AgentReportLevelEnum,
): "up" | "down" => {
  if (current && previous && String(current) === String(previous)) {
    return "up";
  }
  if (current && previous) {
    return normalizeScoreValue(current) >= normalizeScoreValue(previous)
      ? "up"
      : "down";
  }
  if (fallbackLevel) {
    return LEVEL_RANK[fallbackLevel] >= LEVEL_RANK[AgentReportLevelEnum.MID]
      ? "up"
      : "down";
  }
  return "up";
};

const safeString = (...values: MaybeString[]): string => {
  for (const value of values) {
    if (typeof value === "string" && value.trim().length > 0) {
      return value;
    }
  }
  return "";
};

const extractAssetInfo = (
  report: AgentReport,
): {
  symbol: string;
  assetName: string;
  assetIcon?: string;
  assetId: string;
} => {
  const firstAsset = Array.isArray(report.assets)
    ? (report.assets[0] as Record<string, unknown>)
    : undefined;

  const symbol = safeString((firstAsset?.symbol as string) ?? undefined);

  const assetName =
    safeString((firstAsset?.symbol as string) ?? undefined) || "Unknown Asset";

  const assetIcon = safeString((firstAsset?.logo as string) ?? undefined);

  const assetId = safeString((firstAsset?.id as string) ?? undefined);

  return {
    symbol,
    assetName,
    assetIcon,
    assetId,
  };
};

const resolveTimestamp = (report: AgentReport): number => {
  const timestamp = report.content?.time ?? report.created ?? Date.now();
  const coerced = Number(timestamp);
  if (Number.isNaN(coerced)) {
    return Date.now();
  }
  return coerced;
};

export const buildTimelineLabel = (
  timestamp: number | string | Date,
  reference: number | Date | string | Dayjs = dayjs(),
): string => {
  const createdAt = dayjs(timestamp);
  if (!createdAt.isValid()) {
    return "Unknown";
  }
  const referenceDay = dayjs(reference).startOf("day");
  const diffDays = referenceDay.diff(createdAt.startOf("day"), "day");
  if (diffDays === 0) {
    return "Today";
  }
  if (diffDays === 1) {
    return "Yesterday";
  }
  if (diffDays > 1 && diffDays <= 6) {
    return `${diffDays} days ago`;
  }
  return createdAt.format("MMM DD, YYYY");
};

export const transformAgentReport = (
  report?: AgentReport | null,
): ReportTimelineItem | null => {
  if (!report) {
    return null;
  }

  const content = report.content ?? {};
  const { symbol, assetName, assetIcon, assetId } = extractAssetInfo(report);
  const createdTimestamp = resolveTimestamp(report);
  const hasPreviousScore = Boolean(content.previous_level);

  // 计算评分用于判断趋势
  const finalScore =
    content.level ??
    (content.final_score
      ? String(Math.round(content.final_score ?? 0))
      : "N/A");
  const previousScore = content.previous_level;
  const ratingChange = hasPreviousScore
    ? determineRatingTrend(finalScore, previousScore, report.level)
    : undefined;

  return {
    ...report,
    id: report.id ?? `${symbol}-${createdTimestamp}`,
    created: createdTimestamp,
    symbol,
    assetName,
    assetIcon,
    assetId,
    ratingChange,
    hasPreviousScore,
  };
};

export const groupReportsByDate = (
  reports: ReportTimelineItem[],
  reference: number | Date | string | Dayjs = dayjs(),
): ReportsTimelineGroupedData => {
  if (!reports || reports.length === 0) {
    return {};
  }
  const ref = dayjs(reference);
  return [...reports]
    .sort((a, b) => (b.created ?? 0) - (a.created ?? 0))
    .reduce<ReportsTimelineGroupedData>((acc, report) => {
      const label = buildTimelineLabel(report.created ?? Date.now(), ref);
      if (!acc[label]) {
        acc[label] = [];
      }
      acc[label].push(report);
      return acc;
    }, {});
};
